import React, { useState } from 'react';
import axiosInstance from '../../api/axiosInstance';
import type { FriendEntry } from '../../App';
import { IoLogOutOutline } from 'react-icons/io5';
import { formatLastSeen } from '../../utils/dateFormat';

interface FriendListProps {
  friends: FriendEntry[];
  onSelect: (f: FriendEntry) => void;
  onRefresh: () => void;
  onLogout: () => void;
}

export const FriendList: React.FC<FriendListProps> = ({ friends, onSelect, onRefresh, onLogout }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSent, setInviteSent] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviteLoading(true);
    setInviteError(null);
    setInviteSent(null);
    try {
      const { data } = await axiosInstance.post<{ inviteLink: string; receiverEmail: string }>('/api/invite', {
        receiverEmail: email,
      });
      setInviteSent(data.inviteLink);
      setInviteEmail('');
      onRefresh();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to send invite.';
      setInviteError(msg);
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#09090B] bg-[radial-gradient(circle_at_18%_12%,rgba(124,58,237,0.18),transparent_30%),radial-gradient(circle_at_82%_78%,rgba(59,130,246,0.13),transparent_32%)]">
      <div className="bg-[#09090B]/95 border-b border-[#7C3AED]/20 p-4 shadow-lg shadow-[#7C3AED]/10 flex-shrink-0 flex justify-between items-center backdrop-blur">
        <h1 className="font-brand text-xl font-bold text-white">Friends</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setShowInvite(!showInvite); setInviteError(null); setInviteSent(null); }}
            className="px-4 py-2 bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white rounded-xl text-sm font-semibold shadow-lg shadow-[#3B82F6]/20"
          >
            Invite
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-red-500/15 border border-white/10 hover:border-red-400/35 text-[#A1A1AA] hover:text-red-300 rounded-xl text-sm font-semibold transition-all"
            title="Logout"
          >
            <IoLogOutOutline size={18} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      {showInvite && (
        <div className="p-4 bg-[#09090B]/80 border-b border-[#7C3AED]/20 backdrop-blur">
          <form onSubmit={handleInvite} className="flex flex-col gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Friend's email"
              className="w-full px-4 py-2 bg-white/5 border border-[#7C3AED]/25 rounded-xl text-white placeholder-[#A1A1AA] focus:ring-2 focus:ring-[#7C3AED] outline-none"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={inviteLoading}
                className="px-4 py-2 bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] disabled:opacity-50 text-white rounded-xl text-sm font-semibold"
              >
                {inviteLoading ? 'Sending…' : 'Send invite'}
              </button>
              <button
                type="button"
                onClick={() => { setShowInvite(false); setInviteError(null); setInviteSent(null); }}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm"
              >
                Cancel
              </button>
            </div>
            {inviteError && <p className="text-red-400 text-sm">{inviteError}</p>}
            {inviteSent && (
              <p className="text-[#06B6D4] text-sm">
                Invite sent! Share this link: <br />
                <a href={inviteSent} className="underline break-all">{inviteSent}</a>
              </p>
            )}
          </form>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {friends.length === 0 ? (
          <div className="text-center text-[#A1A1AA] py-8">
            No friends yet. Invite someone by email to get started.
          </div>
        ) : (
          <ul className="space-y-2">
            {friends.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => onSelect(f)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#12121A]/90 hover:bg-[#161625] border border-[#7C3AED]/20 hover:border-[#3B82F6]/45 text-left shadow-[0_0_14px_rgba(124,58,237,0.08)] transition-all"
                >
                  <div className="relative flex-shrink-0">
                    {f.avatarUrl ? (
                      <img src={f.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7C3AED]/35 to-[#3B82F6]/20 border border-[#7C3AED]/35 flex items-center justify-center text-white font-medium">
                        {(f.name || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <span
                      className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[#12121A] ${
                        f.isOnline ? 'bg-[#06B6D4]' : 'bg-[#A1A1AA]'
                      }`}
                      aria-label={f.isOnline ? 'Online' : 'Offline'}
                      title={f.isOnline ? 'Online' : 'Offline'}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{f.name}</p>
                    <p className="text-sm text-[#A1A1AA]">
                      {f.isOnline ? (
                        <span className="text-[#06B6D4]">Online</span>
                      ) : (
                        `Last seen ${formatLastSeen(f.lastSeenIst)}`
                      )}
                    </p>
                  </div>
                  {f.unreadCount > 0 && (
                    <span className="min-w-[1.5rem] h-6 px-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white text-xs font-bold flex items-center justify-center shadow-lg shadow-[#06B6D4]/20">
                      {f.unreadCount > 99 ? '99+' : f.unreadCount}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
